/**
 * Profile API: get/update own profile, get another user's profile.
 */

import { getApiUrl } from "./client";
import { toUserMessage } from "../utils/userMessage";

export async function getMyProfile() {
  const res = await fetch(getApiUrl("/users/me/profile"), {
    method: "GET",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(toUserMessage(data?.message, "خطا در دریافت پروفایل"));
  }
  return data?.data ?? null;
}

export async function updateMyProfile(body) {
  const res = await fetch(getApiUrl("/users/me/profile"), {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(toUserMessage(data?.message, "خطا در ذخیره پروفایل"));
  }
  return data?.data ?? null;
}

export async function getUserProfile(userId) {
  const res = await fetch(getApiUrl(`/users/${userId}/profile`), {
    method: "GET",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(toUserMessage(data?.message, "خطا در دریافت پروفایل"));
  }
  return data?.data ?? null;
}

function normalizeUserDisplayName(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ");
}

function userDisplayNameMatches(candidate, target) {
  const normalizedCandidate = normalizeUserDisplayName(candidate);
  const normalizedTarget = normalizeUserDisplayName(target);
  if (!normalizedCandidate || !normalizedTarget) return false;
  return (
    normalizedCandidate === normalizedTarget ||
    normalizedCandidate.includes(normalizedTarget) ||
    normalizedTarget.includes(normalizedCandidate)
  );
}

function pickUserRemoteIdFromSearchResults(users, displayName) {
  if (!Array.isArray(users) || users.length === 0) return null;
  const target = normalizeUserDisplayName(displayName);
  if (!target) return null;

  for (const user of users) {
    if (user?.remote_id == null) continue;
    const labels = [user.full_name, user.name, user.username].filter(Boolean);
    if (labels.some((label) => userDisplayNameMatches(label, target))) {
      const id = Number(user.remote_id);
      if (Number.isFinite(id) && id > 0) return id;
    }
  }

  if (users.length === 1 && users[0]?.remote_id != null) {
    const id = Number(users[0].remote_id);
    if (Number.isFinite(id) && id > 0) return id;
  }

  return null;
}

/**
 * Resolve a user's remote_id from their display name (for forwarded-message profile links).
 */
export async function findUserRemoteIdByDisplayName(displayName) {
  const query = normalizeUserDisplayName(displayName);
  if (!query || query === "کاربر") return null;

  const params = new URLSearchParams();
  params.set("q", query);
  params.set("scope", "users");
  params.set("page", "1");
  if (typeof window !== "undefined") {
    params.set("hostname", window.location.hostname || "");
  }

  const res = await fetch(getApiUrl(`/tickets/search?${params}`), {
    method: "GET",
    credentials: "include",
    headers: { "Cache-Control": "no-cache" },
  });
  const data = await res.json().catch(() => ({}));
  if (res.ok && data?.status === "OK") {
    const sections = Array.isArray(data?.data?.sections) ? data.data.sections : [];
    const userSection = sections.find((section) => section.type === "user");
    const groups = Array.isArray(userSection?.items) ? userSection.items : [];
    const users = groups.map((group) => group?.user).filter(Boolean);
    const fromTicketsSearch = pickUserRemoteIdFromSearchResults(users, query);
    if (fromTicketsSearch != null) return fromTicketsSearch;
  }

  const mentionParams = new URLSearchParams({ q: query });
  const mentionRes = await fetch(getApiUrl(`/users/search?${mentionParams}`), {
    method: "GET",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  const mentionData = await mentionRes.json().catch(() => ({}));
  if (!mentionRes.ok || mentionData?.status !== "OK") return null;

  return pickUserRemoteIdFromSearchResults(
    Array.isArray(mentionData?.data) ? mentionData.data : [],
    query
  );
}
